import React, { useState } from 'react';
import { Upload, Download, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import Modal from './Modal';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const csvCell = (v) => {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// Template = header row, plus an example row when one is supplied so the expected
// format for each column is obvious without reading any documentation.
const downloadTemplate = (headers, filename, sampleRow) => {
  const lines = [headers.map(csvCell).join(',')];
  if (sampleRow?.length) lines.push(sampleRow.map(csvCell).join(','));
  const csv = `${lines.join('\n')}\n`;
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

// Generic bulk-upload modal: pick a CSV/XLSX file, POST it to `endpoint`, show
// a per-row success/failure summary. Reused by Products and Suppliers.
const BulkUploadModal = ({
  open, onClose, title, endpoint, templateHeaders, templateFilename, templateSample,
  requiredColumns = [], onDone,
}) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleClose = () => {
    setFile(null);
    setResult(null);
    onClose();
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post(endpoint, formData);
      const data = res.data.data;
      setResult(data);
      if (data.created > 0) {
        toast.success(`${data.created} of ${data.total} row(s) uploaded`);
        onDone?.();
      }
      if (data.failed > 0) {
        toast.error(`${data.failed} row(s) failed — see details below`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Bulk upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} title={title}>
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-zip-muted">Upload a CSV or Excel file. Column headers should match the template.</p>
          <button
            type="button"
            onClick={() => downloadTemplate(templateHeaders, templateFilename, templateSample)}
            className="flex items-center gap-1 text-xs text-zip-red hover:underline whitespace-nowrap"
          >
            <Download size={12} /> Template
          </button>
        </div>

        {/* The exact columns accepted, so the file can be prepared without guesswork. */}
        <div className="rounded-lg border border-zip-border bg-zip-hover/50 px-3 py-2.5">
          <p className="text-[11px] font-medium text-zip-muted mb-1.5">Columns</p>
          <div className="flex flex-wrap gap-1.5">
            {templateHeaders.map((h) => {
              const required = requiredColumns.includes(h);
              return (
                <span
                  key={h}
                  className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${
                    required
                      ? 'bg-zip-red/10 text-zip-red border-zip-red/25 font-semibold'
                      : 'bg-white text-zip-muted border-zip-border'
                  }`}
                >
                  {h}{required && ' *'}
                </span>
              );
            })}
          </div>
          <p className="text-[10px] text-zip-muted/70 mt-2">
            * required · column order does not matter · header case and spacing are ignored
          </p>
        </div>

        <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-zip-border rounded-xl py-8 cursor-pointer hover:border-zip-red/50 transition-colors">
          <Upload size={20} className="text-zip-muted" />
          <span className="text-sm text-zip-muted">{file ? file.name : 'Click to choose a .csv or .xlsx file'}</span>
          <input
            type="file"
            accept=".csv,.xlsx,.xls"
            className="hidden"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
          />
        </label>

        {result && (
          <div className="max-h-56 overflow-y-auto rounded-lg border border-zip-border divide-y divide-zip-border">
            <div className="px-3 py-2 text-xs font-medium text-zip-muted bg-zip-hover">
              {result.created} created / {result.failed} failed of {result.total}
            </div>
            {result.results.filter((r) => !r.success).map((r, i) => (
              <div key={`e${i}`} className="px-3 py-2 flex items-start gap-2 text-xs">
                <XCircle size={13} className="text-zip-red mt-0.5 flex-shrink-0" />
                <span className="text-zip-muted">Row {r.row}: {r.message}</span>
              </div>
            ))}
            {/* Rows that imported but needed a value corrected — worth seeing, not a failure. */}
            {result.results.filter((r) => r.success && r.warning).map((r, i) => (
              <div key={`w${i}`} className="px-3 py-2 flex items-start gap-2 text-xs">
                <AlertTriangle size={13} className="text-amber-500 mt-0.5 flex-shrink-0" />
                <span className="text-zip-muted">Row {r.row}: {r.warning}</span>
              </div>
            ))}
            {result.failed === 0 && (
              <div className="px-3 py-2 flex items-center gap-2 text-xs text-emerald-600">
                <CheckCircle2 size={13} /> All rows uploaded successfully
              </div>
            )}
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button
            type="button"
            disabled={!file || uploading}
            onClick={handleUpload}
            className="btn-primary flex-1 flex items-center justify-center gap-2"
          >
            {uploading
              ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Uploading…</>
              : 'Upload'}
          </button>
          <button type="button" onClick={handleClose} className="btn-ghost">Close</button>
        </div>
      </div>
    </Modal>
  );
};

export default BulkUploadModal;
